import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/retailer-visitor-shared/lib/async-functions/setTimeoutIfPossible";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { trackBrandLeadsBrandsPageIntroSectionView } from "@faire/web-api--source/events/brandLeads/view/brandsPageIntroSection";
import padStart from "lodash/padStart";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
var ANIMATION_MULTIPLE = 0.8;
var BrandsLandingPageIntroBase = function(props) {
    var _useState = _sliced_to_array(useState(true), 2), shouldAnimate = _useState[0], setShouldAnimate = _useState[1];
    useEffect(function() {
        setTimeoutIfPossible(function() {
            setShouldAnimate(false);
        }, 1000 * props.index * ANIMATION_MULTIPLE);
    }, [
        props.index
    ]);
    return /*#__PURE__*/ _jsx(Content, _object_spread_props(_object_spread({}, props), {
        shouldAnimate: shouldAnimate
    }));
};
var Description = function(param) {
    var description = param.description;
    var _useViewport = useViewport(), isMobile = _useViewport.isMobile, isTabletAndAbove = _useViewport.isTabletAndAbove;
    return /*#__PURE__*/ _jsx(Typography, {
        variant: isTabletAndAbove ? "subheadingSansRegular" : "paragraphSansRegular",
        color: Core.text.subdued,
        align: isMobile ? "center" : "left",
        children: description
    });
};
var Header = function(param) {
    var header = param.header;
    var isMobile = useViewport().isMobile;
    return /*#__PURE__*/ _jsx(Typography, {
        variant: {
            mobileAndAbove: "pageHeaderSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
            desktopAndAbove: "displayLSerifRegular"
        },
        align: isMobile ? "center" : "left",
        children: header
    });
};
var MobileContent = function(param) {
    var index = param.index, header = param.header, description = param.description, shouldAnimate = param.shouldAnimate;
    return /*#__PURE__*/ _jsx(MobileValuePropsRow, {
        num: index,
        header: header,
        description: description,
        shouldAnimate: shouldAnimate
    });
};
var RichContent = function(param) {
    var richContent = param.richContent;
    return /*#__PURE__*/ _jsx(Column, {
        children: richContent
    });
};
var DesktopCopy = function(param) {
    var header = param.header, description = param.description;
    return /*#__PURE__*/ _jsxs(Column, {
        justify: "center",
        children: [
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    width: "384px"
                },
                children: /*#__PURE__*/ _jsx(Header, {
                    header: header
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: "2x"
            }),
            /*#__PURE__*/ _jsx(Row, {
                style: {
                    width: "343px"
                },
                children: /*#__PURE__*/ _jsx(Description, {
                    description: description
                })
            })
        ]
    });
};
var RightImageContent = function(param) {
    var header = param.header, description = param.description, richContent = param.richContent;
    var isTablet = useViewport().isTablet;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(DesktopCopy, {
                header: header,
                description: description
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: isTablet ? "5x" : "10x"
            }),
            /*#__PURE__*/ _jsx(RichContent, {
                richContent: richContent
            })
        ]
    });
};
var LeftImageContent = function(param) {
    var header = param.header, description = param.description, richContent = param.richContent;
    var isTablet = useViewport().isTablet;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(RichContent, {
                richContent: richContent
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                size: isTablet ? "5x" : "10x"
            }),
            /*#__PURE__*/ _jsx(DesktopCopy, {
                header: header,
                description: description
            })
        ]
    });
};
var ContentBase = function(param) {
    var reverse = param.reverse, header = param.header, description = param.description, richContent = param.richContent, index = param.index, shouldAnimate = param.shouldAnimate, eventKey = param.eventKey, startTracking = param.startTracking;
    useEffect(function() {
        startTracking && trackBrandLeadsBrandsPageIntroSectionView(eventKey);
    }, [
        eventKey,
        startTracking
    ]);
    return /*#__PURE__*/ _jsx(ResponsiveContentV2, {
        mobile: /*#__PURE__*/ _jsx(Column, {
            justify: "center",
            style: {
                width: "100%",
                padding: getSpacing(0, "4x")
            },
            children: /*#__PURE__*/ _jsx(MobileContent, {
                index: index,
                header: header,
                description: description,
                shouldAnimate: shouldAnimate
            })
        }),
        tabletAndAbove: /*#__PURE__*/ _jsx(Row, {
            justify: "space-between",
            align: "center",
            style: {
                width: "100%"
            },
            children: reverse ? /*#__PURE__*/ _jsx(LeftImageContent, {
                header: header,
                description: description,
                richContent: richContent
            }) : /*#__PURE__*/ _jsx(RightImageContent, {
                header: header,
                description: description,
                richContent: richContent
            })
        })
    });
};
var Content = observer(ContentBase);
var MobileValuePropsRow = function(param) {
    var num = param.num, header = param.header, description = param.description, shouldAnimate = param.shouldAnimate;
    return /*#__PURE__*/ _jsxs(AnimatedRow, {
        $shouldAnimate: shouldAnimate,
        $index: num - 1,
        children: [
            /*#__PURE__*/ _jsx(Column, {
                children: /*#__PURE__*/ _jsx(Typography, {
                    color: Core.text.subdued,
                    variant: "subheadingSansRegular",
                    children: padStart(num.toString(), 2, "0")
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "2x"
            }),
            /*#__PURE__*/ _jsxs(Column, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.default,
                        variant: "subheadingSansMedium",
                        children: header
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        color: Core.text.default,
                        variant: "paragraphSansRegular",
                        children: description
                    })
                ]
            })
        ]
    });
};
export var BrandsLandingPageIntro = observer(BrandsLandingPageIntroBase);
var FadeIn = keyframes([
    "from{opacity:0;}to{opacity:1;}"
]);
var AnimatedRow = styled(Row).withConfig({
    componentId: "sc-4eef5aa6-0"
})([
    "",
    ""
], function(props) {
    return props.$shouldAnimate ? css([
        "opacity:0;animation:",
        " ",
        "s linear forwards;animation-delay:",
        "s;"
    ], FadeIn, ANIMATION_MULTIPLE, ANIMATION_MULTIPLE * props.$index) : "";
});
